import React, { useCallback, useEffect, useMemo, useState } from "react"
import { ColumnDef, SortingState } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import { createSelector } from "reselect"
import { useDispatch, useSelector } from "react-redux"
import { FormikHelpers, useFormik } from "formik"
import DataTableTemplate from "Components/templates/data-table-template"
import Modal from "Components/molecule/modal"
import ActionButton from "Components/atoms/buttons/action-button"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import IconButton from "Components/atoms/buttons/icon-button"
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container"
import RoleForm from "Components/organisms/forms/role-form"
import RoleFields from "Components/organisms/forms/role-form/types"
import { RoleFormValidation } from "data/role-form"
import Role from "models/role"
import Screen from "models/screen"
import ScreenRole from "models/screen-role"
import {
    AdminRolesSliceState,
    appendToAssignedScreens,
    removeFromAssignedScreens,
    setAssignedScreens,
    setFoucusedRole,
    setIsDeleteRoles,
    setSavingScreens,
    setShowAddRoleModal
} from "slices/admin/roles/reducer"
import { AdminScreenSliceState } from "slices/admin/screens/reducer"
import { deleteRole, getRoles, submitRoleForm, updateRole } from "slices/admin/roles/thunk"
import { getScreens } from "slices/thunk"
import { PencilIcon, HelpIcon } from "Components/atoms/icons"
import useManual from "hooks/useManual"
import useEnterSearch from "hooks/useEnterSearch"
import Chip from "Components/atoms/chip"
import SearchFormContainer from "Components/atoms/containers/search-form-container"
import TextInput from "Components/molecule/inputs/text-input"
import SearchSelect from "Components/molecule/inputs/search-select"

const selectRoles = createSelector(
    (state: any) => state.AdminRoles,
    ({
        roles,
        showAddRoleModal,
        focusedRole,
        assignedScreens,
        savingScreens,
        isDeletingRole
    }: AdminRolesSliceState) => ({
        roles,
        showAddRoleModal,
        focusedRole,
        assignedScreens,
        savingScreens,
        isDeletingRole
    })
)

const selectScreens = createSelector(
    (state: any) => state.AdminScreens,
    ({ screens }: AdminScreenSliceState) => ({
        screens
    })
)

// Screens open ordered by code; the user can re-sort by clicking a column title
const DEFAULT_SCREEN_SORTING: SortingState = [{ id: 'code', desc: false }]

const RolesPage = () => {
    document.title = "Roles | IbexIQ";
    const { handleOpenManual } = useManual("admin/roles");
    const dispatch: any = useDispatch()
    const {
        roles,
        showAddRoleModal,
        focusedRole,
        assignedScreens,
        savingScreens,
        isDeletingRole
    } = useSelector(selectRoles)
    const { screens } = useSelector(selectScreens)

    const [isLoading, setIsLoading] = useState<boolean>(true)

    useEffect(() => {
        const init = async () => {
            await Promise.all([
                dispatch(getRoles()),
                dispatch(getScreens())
            ])
            setIsLoading(false)
        }
        init()
    }, [])

    const onSubmit = async (data: RoleFields, helpers: FormikHelpers<RoleFields>) => {
        helpers.setSubmitting(true)
        await dispatch(submitRoleForm(data));
        helpers.setSubmitting(false)
    }

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setValues,
        setErrors,
        isSubmitting,
        isValid
    } = useFormik({
        initialValues: {
            name: ''
        } as RoleFields,
        onSubmit,
        validationSchema: RoleFormValidation
    })

    const handleOpenAddRoleModal = () => dispatch(setShowAddRoleModal(true))

    const handleCloseAddRoleModal = () => {
        dispatch(setShowAddRoleModal(false))
        setValues({
            name: ''
        }, false)
        setErrors({
            name: undefined
        })
    }

    const handleFocusRole = (role: Role, screens: Screen[]) => {
        const sRs = role.screenRoles;

        // Get screens
        const filteredScreens = screens.filter((s: Screen) => sRs?.some(sR => sR.screenId === s.id))
        dispatch(setAssignedScreens(filteredScreens))
        dispatch(setFoucusedRole(role))
    }

    const handleUnFocusRole = () => {
        dispatch(setFoucusedRole(null))
        dispatch(setAssignedScreens([]))
        screenTable.setSorting(DEFAULT_SCREEN_SORTING)
    }

    const handleUpdateRole = async (role: Role, assignScreens: Screen[]) => {
        dispatch(setSavingScreens(true))
        await dispatch(updateRole(role, assignScreens))
        dispatch(setSavingScreens(false))
    }

    const handleDeleteRole = async (role: Role) => {
        dispatch(setIsDeleteRoles(true))
        await dispatch(deleteRole(role))
        dispatch(setIsDeleteRoles(false))
    }

    const roleColumns = useMemo<ColumnDef<Role>[]>(() => [
        {
            header: "Name",
            accessorKey: "name",
            size: 250,
            cell: ({ row }) => <TableRowControlsContainer style={{ justifyContent: 'space-between' }}>
                {row.original.name}
                <div className='d-flex flex-row justify-content-end'>
                    <IconButton onClick={() => handleFocusRole(row.original, screens)}><PencilIcon /></IconButton>
                </div>
            </TableRowControlsContainer>
        },
        {
            header: "Screens",
            id: "screens",
            size: 500,
            accessorFn: (role: Role) => role.screenRoles?.length ?? 0,
            // One tag per screen, A-Z; wraps instead of truncating
            cell: ({ row }) => <div className="d-flex flex-wrap gap-1 py-1">
                {[...(row.original.screenRoles ?? [])]
                    .map((sR: ScreenRole) => sR.screen?.name ?? '')
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b))
                    .map(name => <Chip key={name} variant="primary">{name}</Chip>)}
            </div>,
            meta: { cellClassName: "text-wrap overflow-visible" }
        }
    ], [screens])

    // Filters: role name and "has screen". They apply on Search / Enter, like every other filter bar.
    const [roleNameFilter, setRoleNameFilter] = useState('')
    const [screenFilter, setScreenFilter] = useState('')
    const [appliedFilters, setAppliedFilters] = useState({ roleName: '', screenId: '' })
    const handleSearch = () => setAppliedFilters({ roleName: roleNameFilter, screenId: screenFilter })
    const handleClearSearch = () => {
        setRoleNameFilter('')
        setScreenFilter('')
        setAppliedFilters({ roleName: '', screenId: '' })
    }
    const { inputProps, selectProps } = useEnterSearch(handleSearch)
    const filteredRoles = useMemo(() => roles.filter((r: Role) =>
        r.name.toLowerCase().includes(appliedFilters.roleName.trim().toLowerCase())
        && (!appliedFilters.screenId || r.screenRoles?.some((sR: ScreenRole) => sR.screenId === appliedFilters.screenId))
    ), [roles, appliedFilters])

    const screenOptions = useMemo(() => [
        { value: '', label: 'All' },
        ...[...screens].sort((a: Screen, b: Screen) => a.name.localeCompare(b.name)).map((s: Screen) => ({ value: s.id, label: s.name })),
    ], [screens])

    const roleTable = useDataTable({
        columns: roleColumns,
        data: filteredRoles,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    const isAssigned = useCallback((screen: Screen) =>
        assignedScreens.some((s: Screen) => s.id === screen.id), [assignedScreens])

    const screenColumns = useMemo<ColumnDef<Screen>[]>(() => [
        {
            header: "Assigned",
            id: "assigned",
            size: 90,
            accessorFn: (screen: Screen) => isAssigned(screen) ? 1 : 0,
            cell: ({ row }) => <input
                type="checkbox"
                checked={isAssigned(row.original)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                        dispatch(appendToAssignedScreens(row.original))
                    } else {
                        dispatch(removeFromAssignedScreens(row.original))
                    }
                }}
            />
        },
        {
            header: "Name",
            accessorKey: "name",
            size: 220
        },
        {
            header: "Code",
            accessorKey: "code",
            size: 150
        },
        {
            header: "Description",
            accessorKey: "description",
            size: 400
        }
    ], [isAssigned])

    const screenTable = useDataTable({
        columns: screenColumns,
        data: screens,
        initialSorting: DEFAULT_SCREEN_SORTING,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    const hasChangeInScreen = useMemo(() => {
        if (!focusedRole) return false;

        const focusedIds = new Set(focusedRole.screenRoles?.map(sr => sr.screenId) ?? []);
        const assignedIds = new Set(assignedScreens.map(s => s.id));

        if (focusedIds.size !== assignedIds.size) {
            return true;
        }

        for (const id of focusedIds) {
            if (!assignedIds.has(id)) {
                return true;
            }
        }

        return false;
    }, [focusedRole, assignedScreens]);

    if (focusedRole) {
        return <DataTableTemplate
            title="Admin"
            breadcrumbItem={`Roles / ${focusedRole.name}`}
            table={screenTable}
            columns={screenColumns}
            isLoading={false}
            loadingTitle="Loading Screens"
            actions={<>
                {hasChangeInScreen && <PrimaryButton
                    onClick={() => handleUpdateRole(focusedRole, assignedScreens)}
                    disabled={savingScreens}
                >
                    {savingScreens ? 'Saving...' : 'Save'}
                </PrimaryButton>}
                {!savingScreens && <SecondaryButton onClick={handleUnFocusRole}>Back</SecondaryButton>}
                <ActionButton
                    disabled={isDeletingRole}
                    style={{ backgroundColor: 'red' }}
                    onClick={() => handleDeleteRole(focusedRole)}
                >
                    {isDeletingRole ? 'Deleting...' : 'Delete'}
                </ActionButton>
                <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
            </>}
        />
    }

    return <DataTableTemplate
        title="Admin"
        breadcrumbItem="Roles"
        table={roleTable}
        columns={roleColumns}
        isLoading={isLoading}
        loadingTitle="Loading Roles"
        filters={<SearchFormContainer
            inputs={<>
                <TextInput label="Role" placeholder="Role name" value={roleNameFilter} onChange={(e) => setRoleNameFilter(e.target.value)} {...inputProps} />
                <SearchSelect label="Has Screen" options={screenOptions} value={screenFilter} onChange={(e) => setScreenFilter(e?.value ?? '')} {...selectProps} />
            </>}
            clearSearchButton={<>
                <PrimaryButton onClick={handleSearch}>Search</PrimaryButton>
                <SecondaryButton onClick={handleClearSearch}>Clear Search</SecondaryButton>
            </>}
        />}
        actions={<>
            <ActionButton onClick={handleOpenAddRoleModal}>Add Role</ActionButton>
            <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
        </>}
        modal={<Modal
            isOpen={showAddRoleModal}
            onClose={handleCloseAddRoleModal}
            title="Create Role"
        >
            <RoleForm
                edit={false}
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                handleSubmit={handleSubmit}
                isValid={isValid}
                isSubmitting={isSubmitting}
            />
        </Modal>}
    />
}


export default RolesPage
